// App-level MRU imports
import { BottomBanner } from '@chiroup/components/BottomBanner';
import * as Sentry from '@sentry/react';
import { AppMRUProvider } from './contexts/appMru.context';
// import { Hub } from 'aws-amplify/utils';
import { MILLISECONDS } from '@/libs/core/src/lib/constants/time';
import { getCurrentUser } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { StopListenerCallback } from '@aws-amplify/core/dist/esm/Hub/types';
import { resetUnauthorized } from '@chiroup/client-core/functions/ChiroUpAPI';
import { Loading } from '@chiroup/components/Loading';
import { LoadingPage } from '@chiroup/components/LoadingPage';
import { useNativeTheme } from '@chiroup/hooks/useNativeTheme';
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import MfaResetGate from './components/common/mfa/MfaResetGate';
import ThirdPartyScripts from './components/common/ThirdPartyScripts';
import Toasts from './components/common/toasts/Toasts';
import AppMRUDrawerLoader from './components/layout/AppMRUDrawerLoader';
import Container from './components/layout/Container';
import SwitchLocations from './components/layout/SwitchLocations';
import PayJsManager from './components/payments/PayJsManager';
import { ManagedActionItemsProvider } from './contexts/managedActionItems.context';
import { ThemeContext } from './contexts/theme.context';
import { ToastProvider } from './contexts/toast.context';
import userService from './services/user.service';
import { enhancedSignOut, recoverFromTokenRefreshFailure } from './utils/authUtils';

/**
 * sessionStorage key set on a forced sign-out so the Login page can render a
 * friendly explanation banner. Cleared after the banner shows once. Survives
 * `enhancedSignOut` because that helper only filters cognito/amplify/auth
 * keys when cleaning up sessionStorage.
 */
export const SESSION_EXPIRED_FLAG = 'chiroup.sessionExpiredAt';
// Route-level lazy modules
const MarketingAddOns = lazy(() => import('./components/marketing-add-ons/MarketingAddOns'));
const OAuthConfirm = lazy(() => import('./components/oauth/OAuthConfirm'));
const OAuthInitiate = lazy(() => import('./components/oauth/OAuthInitiate'));
const ConsolidatedTransactionModal = lazy(
  () => import('./components/patients/detail/billing/transactions/ConsolidatedTransactionModal')
);
const InvoiceRoute = lazy(() => import('./components/billing/InvoiceRoute'));
const loadScheduleWrapper = () => import('./components/schedule/ScheduleWrapper');
const ScheduleWrapper = lazy(loadScheduleWrapper);

const preloadAuthenticatedApp = () => {
  const isSupportAccessLaunch =
    window.location.pathname === '/support-access' && window.location.hash.includes('token=');

  if (isSupportAccessLaunch) return;

  void import('./components/layout/ClinicSelectedContainer').catch(() => {});

  if (window.location.pathname.startsWith('/schedule')) {
    void loadScheduleWrapper().catch(() => {});
  }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare const payJS: any;

const SentryRoutes = Sentry.wrapReactRouterRouting(Routes);

const Home = lazy(() => import('./components/home/Home'));
const DashboardRoute = lazy(() => import('./components/widgets/DashboardRoute'));
const Billing = lazy(() => import('./components/billing/BillingWrapper'));
const Reporting = lazy(() => import('./components/reporting/ReportingContainer'));
const Patients = lazy(() => import('./components/patients/Patients'));
const ClinicalSkills = lazy(() => import('./components/clinical-skills/ClinicalSkills'));
const PracticeResources = lazy(() => import('./components/practice-resources/PracticeResources'));
const Settings = lazy(() => import('./components/settings/Settings'));
const ReportContainer = lazy(() => import('./components/report/ReportContainer'));
const Admin = lazy(() => import('./components/admin/Admin'));
const LoggedOut = lazy(() => import('./components/logged-out/LoggedOut'));
const Research = lazy(() => import('./components/research/Research'));
const FeedbackHub = lazy(() => import('./pages/FeedbackHub'));
const FeatureRequestDetail = lazy(() => import('./pages/FeatureRequestDetail'));
const FeatureRequestsList = lazy(() => import('./components/support/features/FeatureRequestsList'));
const SupportHub = lazy(() => import('./pages/SupportHub'));
const SupportCases = lazy(() => import('./pages/SupportCases'));
const SupportAccess = lazy(() => import('./pages/SupportAccess'));
const SupportAccessLanding = lazy(() => import('./components/support/access/SupportAccessLanding'));
const MRUExamplePage = lazy(() => import('./pages/MRUExamplePage'));
const MessagingPage = lazy(() => import('./pages/MessagingPage'));
const TeamChat = lazy(() => import('./components/team-chat/TeamChatPage'));
const AiReceptionistPage = lazy(() => import('./pages/AiReceptionistPage'));
const MfaForceEnroll = lazy(() => import('./components/common/mfa/MfaForceEnroll'));

// const WhatsNew = lazy(() => import('./components/whats-new/WhatsNew'));

// Translate backend session-lifecycle responses into window events so the
// InactivityLockProvider (and other listeners) can react without each call
// site having to inspect every error shape.
//
//   - 423 sessionLocked  → 'chiroup:session-locked' (inactivity lock fired)
//   - 401 sessionExpired → 'chiroup:session-expired' (forged/malformed JWT)
//
// ChiroUpAPI rejects with the status code in `response.data.statusCode`; other
// error sources (raw axios, direct fetch wrappers) put it on `response.status`.
// Check both — matches `usePatient`'s `error.response?.status || error.response?.data?.statusCode`.
const dispatchIfSessionLifecycle = (error: unknown) => {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: { statusCode?: number; sessionLocked?: boolean; sessionExpired?: boolean };
      };
    }
  )?.response;

  if (!response) return;

  const status = response.status ?? response.data?.statusCode;

  if (status === 423 && response.data?.sessionLocked) {
    window.dispatchEvent(new CustomEvent('chiroup:session-locked'));
  } else if (status === 401 && response.data?.sessionExpired) {
    window.dispatchEvent(new CustomEvent('chiroup:session-expired'));
  }
};

// Theme is a per-browser preference, so it is stored under a fixed key rather
// than the per-user key from `useLocalStorage`: that hook reads `MeContext`,
// whose provider lives in `Container` (a child of `App`), so from here it
// would always see no user and silently no-op.
const THEME_LS_KEY = 'chiroup:theme';

const readSavedTheme = (): 'light' | 'dark' | undefined => {
  try {
    const saved = localStorage.getItem(THEME_LS_KEY);
    return saved === 'light' || saved === 'dark' ? saved : undefined;
  } catch {
    return undefined;
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: dispatchIfSessionLifecycle }),
  mutationCache: new MutationCache({ onError: dispatchIfSessionLifecycle }),
  defaultOptions: {
    queries: {
      staleTime: MILLISECONDS.inMinutes(5),
      // Limit how long inactive query data stays in memory.
      // The default is 5 minutes; lowering to 2 minutes reduces peak heap
      // usage when navigating between heavy pages (e.g. patient billing → dashboard).
      cacheTime: MILLISECONDS.inMinutes(2),
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  const [authState, setAuthState] = useState<string>();
  const [user, setUser] = useState<any>();
  const nativeTheme = useNativeTheme();
  const [theme, setTheme] = useState<'light' | 'dark'>(() => readSavedTheme() || 'light');
  const [appLoading, setAppLoading] = useState<boolean>(false);

  // Hoisted so both the session-expired listener and the auth-Hub listener
  // below can touch it. `enhancedSignOut()` does NOT reload the page, so the
  // App component stays mounted across sign-out → sign-in cycles within a
  // single tab — without resetting this on each auth transition, a second
  // session in the same tab can hit the 12h cap and the handler short-
  // circuits forever.
  const sessionExpiryHandled = useRef(false);

  const shouldRender = useMemo(() => {
    return ['local', 'dev', 'staging'].includes(import.meta.env.VITE_STAGE);
  }, []);
  const isSupportAccessLaunch =
    window.location.pathname === '/support-access' && window.location.hash.includes('token=');

  // pay.js script loading handled by PayJsManager inside Router

  const toggleScheme = () => {
    const themeToUse = theme === 'light' ? 'dark' : 'light';
    setTheme(themeToUse);

    try {
      if (themeToUse === nativeTheme) {
        localStorage.removeItem(THEME_LS_KEY);
      } else {
        localStorage.setItem(THEME_LS_KEY, themeToUse);
      }
    } catch {
      // Storage unavailable — the in-memory theme still applies for this session.
    }
  };

  useEffect(() => {
    let isDestroyed = false;
    let stopListener: StopListenerCallback;
    let tokenRefreshRevalidationInFlight = false;

    const doIt = async () => {
      const handleTokenRefreshFailure = async () => {
        if (tokenRefreshRevalidationInFlight) return;

        tokenRefreshRevalidationInFlight = true;

        try {
          const revalidated = await recoverFromTokenRefreshFailure();

          if (!revalidated && !isDestroyed) {
            setAuthState('signedOut');
          }
        } finally {
          tokenRefreshRevalidationInFlight = false;
        }
      };

      stopListener = Hub.listen('auth', data => {
        if (!isDestroyed) {
          if (data.payload.event === 'tokenRefresh_failure') {
            void handleTokenRefreshFailure();
          } else if (data.payload.event === 'signedOut') {
            queryClient.clear();
            // Reset session-expired latch so the NEXT session (same tab,
            // since enhancedSignOut doesn't reload) gets its own one-shot.
            sessionExpiryHandled.current = false;
            setAuthState('signedOut');
          } else if (data.payload.event === 'signedIn') {
            preloadAuthenticatedApp();
            // Re-arm the global session-invalid interceptor latch. The SPA can
            // complete a sign-out/sign-in cycle without a full page reload, so
            // without this the auto-sign-out handler would fire only once per
            // page load. See resetUnauthorized in apiInterceptor.ts.
            resetUnauthorized();
            // New auth lifecycle — reset the latch unconditionally in case
            // the signedOut event was missed (e.g., on a cold boot that
            // proceeds straight from authState=undefined to authenticated).
            sessionExpiryHandled.current = false;
            // Garbage-collect LockedSession rows from this user's PRIOR
            // sign-ins. Must live here (not inside InactivityLockProvider)
            // because the provider only mounts after /me resolves — by then
            // Amplify Hub has already delivered the signedIn event, and v6
            // does not replay past events to late subscribers, so the GC
            // would never run from there.
            userService.sessionResume().catch(() => {
              // Best-effort. A failed cleanup leaves a stale LockedSession
              // row that's harmless (it can only ever match its own old
              // authTime, which no current token carries).
            });
            setAuthState('authenticated');
            setUser(data.payload.data);
          }
        }
      });

      const currentUser = await getCurrentUser().catch(err => {});

      if (currentUser && !isDestroyed) {
        preloadAuthenticatedApp();
        setAuthState('authenticated');
        setUser(currentUser);
      } else if (!isDestroyed) {
        // Avoid rendering (and downloading) the signed-out app while the
        // initial auth check is still in flight. Preserve a Hub event that may
        // have authenticated the user while getCurrentUser was resolving.
        setAuthState(current => current ?? 'signedOut');
      }
    };

    doIt();

    return () => {
      isDestroyed = true;
      stopListener?.();
    };
  }, []);

  useEffect(() => {
    setTheme(readSavedTheme() || (nativeTheme === 'dark' ? 'dark' : 'light'));
  }, [nativeTheme]);

  // Note: Location switcher keyboard shortcut (Ctrl+Shift+L) is now handled directly in SwitchLocations.tsx

  useEffect(() => {
    document.body.classList.add('dark:bg-dark-gray-900');
    document.body.classList.add('print:bg-white');
    document.body.classList.add('print:dark:bg-white');
    const method = theme === 'dark' ? 'add' : 'remove';
    document.documentElement.classList[method]('dark');
  }, [theme]);

  // Session-expired sign-out handler. Lives at the App level so it works in
  // ALL states: on a cold boot when /me itself returns 401 sessionExpired
  // (Container hasn't rendered yet, so InactivityLockProvider isn't mounted),
  // and in-session when any query/mutation hits 401. Today the only server
  // condition that produces this response is the wrapper's missing-auth_time
  // fail-closed (forged/malformed JWT). Dedupe with sessionExpiryHandled so
  // multiple simultaneous 401s only trigger one sign-out; reset in the Hub
  // listener below on each new auth transition.
  useEffect(() => {
    const onSessionExpired = () => {
      if (sessionExpiryHandled.current) return;
      sessionExpiryHandled.current = true;

      try {
        sessionStorage.setItem(SESSION_EXPIRED_FLAG, String(Date.now()));
      } catch {
        // ignore — banner just won't show, sign-out still proceeds
      }

      void enhancedSignOut();
    };

    window.addEventListener('chiroup:session-expired', onSessionExpired);

    return () => window.removeEventListener('chiroup:session-expired', onSessionExpired);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppMRUProvider>
        <ManagedActionItemsProvider>
          <ToastProvider>
            <ThemeContext.Provider
              value={{
                theme,
                toggleScheme,
              }}
            >
              <>
                <BrowserRouter>
                  <PayJsManager />
                  <ThirdPartyScripts shouldLoad={true} />
                  {appLoading || authState === undefined ? (
                    <LoadingPage />
                  ) : authState === 'authenticated' && user && isSupportAccessLaunch ? (
                    <Suspense fallback={<Loading size={5} />}>
                      <SupportAccessLanding />
                    </Suspense>
                  ) : authState === 'authenticated' && user ? (
                    <Container>
                      <Suspense fallback={<Loading size={5} />}>
                        {/* App-level MRU drawer placed at root level but inside AppMRUProvider */}
                        <AppMRUDrawerLoader enabled={shouldRender} />
                        <MfaResetGate>
                          <SentryRoutes>
                            <Route path="/" element={<Home />} />
                            <Route path="/dashboard" element={<DashboardRoute />} />
                            <Route path="/widgets" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/billing/*" element={<Billing />} />
                            <Route path="/reporting/*" element={<Reporting />} />
                            <Route path="/patients/*" element={<Patients />} />
                            <Route path="/schedule/*" element={<ScheduleWrapper />} />
                            <Route path="/channels/*" element={<TeamChat />} />
                            <Route path="/ai-receptionist/*" element={<AiReceptionistPage />} />
                            <Route path="/messaging/*" element={<MessagingPage />} />
                            <Route path="/messaging/:conversationId" element={<MessagingPage />} />
                            <Route path="/research/*" element={<Research />} />
                            <Route path="/report/*" element={<ReportContainer />} />
                            {shouldRender ? (
                              <Route path="/mru-example" element={<MRUExamplePage />} />
                            ) : null}
                            <Route path="/clinical-skills/*" element={<ClinicalSkills />} />
                            <Route path="/practice-resources/*" element={<PracticeResources />} />
                            <Route path="/settings/mfa/setup" element={<MfaForceEnroll />} />
                            <Route
                              path="/settings/*"
                              element={<Settings theme={theme} toggleScheme={toggleScheme} />}
                            />
                            <Route path="/admin/*" element={<Admin />} />
                            {import.meta.env.VITE_STAGE !== 'sandbox' && (
                              <Route path="/marketing-add-ons/*" element={<MarketingAddOns />} />
                            )}
                            <Route
                              path="consolidated/transaction/*"
                              element={<ConsolidatedTransactionModal mode={'route'} />}
                            />
                            <Route path="/invoice/:invoiceId" element={<InvoiceRoute />} />
                            <Route path="/oauth/confirm" element={<OAuthConfirm />} />
                            <Route path="/oauth/initiate" element={<OAuthInitiate />} />
                            <Route path="/support-hub" element={<SupportHub />} />
                            <Route path="/support-hub/access" element={<SupportAccess />} />
                            <Route path="/support/cases" element={<SupportCases />} />
                            <Route path="/support-access" element={<SupportAccessLanding />} />
                            <Route path="/feedback" element={<FeedbackHub />}>
                              <Route index element={<FeatureRequestsList />} />
                              <Route path="features" element={<FeatureRequestsList />} />
                              <Route path="features/:id" element={<FeatureRequestDetail />} />
                            </Route>
                          </SentryRoutes>
                        </MfaResetGate>
                      </Suspense>
                    </Container>
                  ) : (
                    <Suspense fallback={<Loading size={5} />}>
                      <div className="h-full flex flex-col">
                        <div style={{ minHeight: 'calc(100vh - 100px)' }}>
                          <LoggedOut
                            authState={authState}
                            setAppLoading={setAppLoading}
                            theme={theme}
                          />
                        </div>
                      </div>
                    </Suspense>
                  )}
                </BrowserRouter>
                <Toasts />
                <SwitchLocations />
              </>
            </ThemeContext.Provider>
          </ToastProvider>
        </ManagedActionItemsProvider>
      </AppMRUProvider>
      {import.meta.env.VITE_STAGE === 'sandbox' && (
        <BottomBanner message="You are in sandbox mode. Do not use real data." />
      )}
    </QueryClientProvider>
  );
}

export default App;
